import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  PaperPlaneTilt,
  Warning,
  Robot,
  User as UserIcon,
  Check,
  ArrowUUpLeft,
  Envelope as EnvelopeIcon,
} from "@phosphor-icons/react";
import {
  fetchConversations,
  fetchConversation,
  postConversationOwnerReply,
  archiveConversation,
  unarchiveConversation,
  markConversationUnread,
  markConversationRead,
} from "@/lib/api";
import { toast } from "sonner";
import ChannelAvatar, { ChannelBadge } from "@/components/ChannelAvatar";
import { useAuth } from "@/lib/auth";

const CHANNEL_FILTERS = [
  { key: "all", label: "Alle" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "email", label: "Email" },
  { key: "simulator", label: "Test" },
];

const STATUS_FILTERS = [
  { key: "all", label: "Alle" },
  { key: "unread", label: "Ungelesen" },
  { key: "escalated", label: "Eskaliert" },
  { key: "archived", label: "Erledigt" },
];

const CHANNEL_LABEL = {
  whatsapp: "WhatsApp",
  email: "Email",
  simulator: "Simulator",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "jetzt";
  if (mins < 60) return `${mins} Min.`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} Std.`;
  return `${Math.floor(h / 24)} Tg.`;
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Heute";
  if (d.toDateString() === yesterday.toDateString()) return "Gestern";
  return d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" });
}

function StatusDot({ conv }) {
  if (conv.has_open_escalation)
    return <span className="w-2.5 h-2.5 rounded-full bg-destructive shrink-0" />;
  return null;
}

export default function Inbox() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("filter") || "all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const chatBottomRef = useRef(null);

  const loadList = async () => {
    const params = {};
    if (channelFilter !== "all") params.channel = channelFilter;
    if (statusFilter === "archived") params.archived = "only";
    let data = await fetchConversations(params);
    if (statusFilter === "escalated") data = data.filter((c) => c.has_open_escalation);
    if (statusFilter === "unread") data = data.filter((c) => c.is_unread || c.has_open_escalation);
    setConvos(data);
  };

  const loadDetail = async () => {
    if (!id) {
      setDetail(null);
      return;
    }
    const d = await fetchConversation(id);
    setDetail(d);
    // Auto-mark als gelesen wenn Convo geöffnet wird
    if (d.is_unread) {
      markConversationRead(id).then(() => loadList()).catch(() => {});
    }
    setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const handleArchiveToggle = async () => {
    if (!detail) return;
    try {
      if (detail.archived_at) {
        await unarchiveConversation(detail.id);
        toast.success("Wieder geöffnet");
      } else {
        await archiveConversation(detail.id);
        toast.success("Als erledigt markiert");
      }
      loadList();
      navigate("/inbox");
    } catch {
      toast.error("Fehlgeschlagen");
    }
  };

  const handleMarkUnread = async () => {
    if (!detail) return;
    try {
      await markConversationUnread(detail.id);
      toast.success("Als ungelesen markiert");
      navigate("/inbox");
      loadList();
    } catch {
      toast.error("Fehlgeschlagen");
    }
  };

  useEffect(() => {
    loadList();
    // Auto-Refresh alle 10s — neue Nachrichten kommen rein
    const t = setInterval(loadList, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, channelFilter]);

  useEffect(() => {
    loadDetail();
    if (id) {
      const t = setInterval(loadDetail, 10000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFilter = (key) => {
    setStatusFilter(key);
    if (key === "all") searchParams.delete("filter");
    else searchParams.set("filter", key);
    setSearchParams(searchParams);
  };

  const handleSendOwnerReply = async () => {
    if (!reply.trim() || !detail) return;
    setSending(true);
    try {
      const res = await postConversationOwnerReply(detail.id, reply.trim());
      setReply("");
      toast.success("Antwort an Kunde gesendet.");
      if (res.delivery_error) toast.error(`Versand-Problem: ${res.delivery_error}`);
      await loadDetail();
      loadList();
    } catch {
      toast.error("Antwort konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  // Build timeline: chronologisch ein flacher Stream aus Bubbles (customer / bot / owner)
  const buildBubbles = (msgs) => {
    const bubbles = [];
    for (const m of msgs || []) {
      if (m.body) {
        bubbles.push({
          kind: "customer",
          text: m.body,
          ts: m.created_at,
          msgId: m.id,
        });
      }
      if (m.ai_reply) {
        bubbles.push({
          kind: "bot",
          text: m.ai_reply,
          ts: m.replied_at || m.created_at,
          msgId: m.id,
          escalated: m.status === "escalated",
          internalNotes: m.internal_notes || "",
        });
      } else if (m.status === "escalated" && m.body) {
        bubbles.push({
          kind: "escalation",
          text: "Bot war unsicher — du musst antworten",
          ts: m.created_at,
          msgId: m.id,
        });
      }
      if (m.owner_reply) {
        bubbles.push({
          kind: "owner",
          text: m.owner_reply,
          ts: m.owner_replied_at || m.replied_at || m.created_at,
          msgId: m.id,
          byEmail: m.owner_reply_by_email || null,
        });
      }
    }
    return bubbles;
  };

  const bubbles = detail ? buildBubbles(detail.messages) : [];

  return (
    <div
      className="flex h-[calc(100vh-3.5rem)] md:h-[calc(100vh-4rem)] bg-background"
      data-testid="inbox-page"
    >
      {/* ---------- LIST ---------- */}
      <aside
        className={`flex-col border-r border-border-soft bg-white w-full md:w-[360px] md:flex ${
          detail ? "hidden md:flex" : "flex"
        }`}
      >
        <div className="p-4 border-b border-border-soft space-y-3">
          <div className="flex gap-1.5 bg-muted rounded-xl p-1.5">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => handleFilter(f.key)}
                className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  statusFilter === f.key
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {CHANNEL_FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`channel-filter-${f.key}`}
                onClick={() => setChannelFilter(f.key)}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all ${
                  channelFilter === f.key
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {convos.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Keine Konversationen.
            </div>
          )}
          {convos.map((c) => {
            const active = detail?.id === c.id;
            const preview =
              c.last_owner_reply || c.last_ai_reply || c.last_body || "(leer)";
            const previewPrefix = c.last_owner_reply
              ? "Du: "
              : c.last_ai_reply
              ? "Bot: "
              : "";
            const showUnread = c.is_unread || c.has_open_escalation;
            return (
              <Link
                key={c.id}
                to={`/inbox/${c.id}`}
                data-testid={`inbox-item-${c.id}`}
                className={`flex gap-3 px-3 py-3 mx-2 my-0.5 rounded-xl transition-all ${
                  active
                    ? "bg-secondary shadow-sm"
                    : "hover:bg-secondary/50"
                }`}
              >
                <ChannelAvatar channel={c.channel} name={c.sender_name} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className={`truncate ${showUnread ? "font-semibold" : "font-medium"}`}>
                      {c.sender_name}
                    </div>
                    <div className="text-[11px] text-muted-foreground shrink-0">
                      {timeAgo(c.last_message_at)}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.sender_id} · {c.msg_count} Nachricht{c.msg_count !== 1 ? "en" : ""}
                    {c.archived_at && " · erledigt"}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className={`text-sm line-clamp-1 ${showUnread ? "text-foreground" : "text-muted-foreground"}`}>
                      {previewPrefix}
                      {preview}
                    </div>
                    {c.has_open_escalation ? (
                      <span className="w-2.5 h-2.5 rounded-full bg-destructive shrink-0" title="Eskaliert" />
                    ) : c.is_unread ? (
                      <span className="w-2.5 h-2.5 rounded-full bg-primary shrink-0" title="Ungelesen" />
                    ) : null}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </aside>

      {/* ---------- DETAIL ---------- */}
      <section
        className={`flex-1 flex-col bg-secondary/30 ${
          detail ? "flex" : "hidden md:flex"
        }`}
      >
        {!detail && (
          <div className="hidden md:flex h-full flex-col items-center justify-center p-12 text-center">
            <div className="w-20 h-20 rounded-2xl bg-white shadow-card border border-border-soft flex items-center justify-center mb-6 relative">
              <Robot size={36} weight="duotone" className="text-primary" />
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary border-2 border-white" />
            </div>
            <h3 className="font-display font-semibold text-2xl max-w-md">
              Wähle eine Konversation
            </h3>
            <p className="text-muted-foreground text-sm mt-3 max-w-sm leading-relaxed">
              Hier werden alle Konversationen von Lulu Bot angezeigt. Du kannst jederzeit
              eingreifen oder die Automatisierung überwachen.
            </p>
          </div>
        )}

        {detail && (
          <>
            {/* Chat-Header */}
            <div className="bg-white border-b border-border-soft px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
              <button
                onClick={() => navigate("/inbox")}
                className="md:hidden p-1 -ml-1 hover:bg-secondary rounded-lg transition-colors"
                data-testid="back-to-inbox"
                aria-label="Zurück"
              >
                <ArrowLeft size={20} weight="bold" />
              </button>
              <ChannelAvatar channel={detail.channel} name={detail.sender_name} size={40} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {detail.sender_name}
                  {detail.archived_at && (
                    <span className="ml-2 text-xs text-muted-foreground font-normal">(erledigt)</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate flex items-center gap-2">
                  <ChannelBadge channel={detail.channel} withLabel={true} size={10} />
                  <span>·</span>
                  <span className="truncate">{detail.sender_id}</span>
                </div>
              </div>
              <button
                onClick={handleMarkUnread}
                className="p-2 hover:bg-secondary rounded-lg text-muted-foreground transition-colors"
                title="Als ungelesen markieren"
                aria-label="Als ungelesen markieren"
              >
                <EnvelopeIcon size={18} weight="duotone" />
              </button>
              <button
                onClick={handleArchiveToggle}
                className="p-2 hover:bg-secondary rounded-lg text-muted-foreground transition-colors"
                title={detail.archived_at ? "Wieder öffnen" : "Als erledigt markieren"}
                aria-label={detail.archived_at ? "Wieder öffnen" : "Als erledigt markieren"}
              >
                {detail.archived_at ? (
                  <ArrowUUpLeft size={18} weight="duotone" />
                ) : (
                  <Check size={18} weight="duotone" />
                )}
              </button>
            </div>

            {/* Chat-Body */}
            <div className="flex-1 overflow-auto px-3 sm:px-6 py-6">
              <div className="max-w-2xl mx-auto space-y-3">
                {bubbles.length === 0 && (
                  <div className="text-center text-muted-foreground text-sm py-8">
                    Noch keine Nachrichten.
                  </div>
                )}
                {bubbles.map((b, idx) => {
                  // Date-Separator wenn Tag wechselt
                  const prev = bubbles[idx - 1];
                  const showDate =
                    !prev ||
                    new Date(prev.ts).toDateString() !== new Date(b.ts).toDateString();

                  if (b.kind === "escalation") {
                    return (
                      <div key={idx}>
                        {showDate && (
                          <div className="text-center my-4">
                            <span className="inline-block bg-white border border-border-soft shadow-sm rounded-full px-3 py-1 text-[11px] text-muted-foreground">
                              {formatDateLabel(b.ts)}
                            </span>
                          </div>
                        )}
                        <div className="text-center my-2">
                          <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive rounded-full px-3 py-1.5 text-xs">
                            <Warning size={14} weight="fill" />
                            {b.text}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const isLeft = b.kind === "customer";
                  return (
                    <div key={idx}>
                      {showDate && (
                        <div className="text-center my-4">
                          <span className="inline-block bg-white border border-border rounded-full px-3 py-1 text-[11px] text-muted-foreground">
                            {formatDateLabel(b.ts)}
                          </span>
                        </div>
                      )}
                      <div
                        className={`flex gap-2 items-end ${
                          isLeft ? "" : "justify-end"
                        }`}
                      >
                        {isLeft && (
                          <ChannelAvatar
                            channel={detail.channel}
                            name={detail.sender_name}
                            size={28}
                          />
                        )}
                        <div className="max-w-[75%]">
                          <div
                            className={`rounded-2xl px-4 py-2.5 shadow-sm whitespace-pre-wrap ${
                              b.kind === "customer"
                                ? "bg-white border border-border-soft rounded-bl-sm"
                                : b.kind === "bot"
                                ? "bg-primary text-primary-foreground rounded-br-sm"
                                : "bg-emerald-600 text-white rounded-br-sm"
                            }`}
                          >
                            {b.text}
                          </div>
                          <div
                            className={`text-[10px] text-muted-foreground mt-1 ${
                              isLeft ? "ml-2" : "mr-2 text-right"
                            }`}
                          >
                            {b.kind === "customer"
                              ? formatTime(b.ts)
                              : b.kind === "bot"
                              ? `Bot · ${formatTime(b.ts)}`
                              : (() => {
                                  const isMe =
                                    b.byEmail && currentUser?.email &&
                                    b.byEmail.toLowerCase() === currentUser.email.toLowerCase();
                                  const who = !b.byEmail
                                    ? "Du"
                                    : isMe
                                    ? "Du"
                                    : b.byEmail.split("@")[0];
                                  return `${who} · ${formatTime(b.ts)}`;
                                })()}
                          </div>
                          {b.kind === "bot" && b.internalNotes && (
                            <div
                              className="mt-2 mr-2 border-l-2 border-amber-400 bg-amber-50/60 px-3 py-2 text-xs font-mono text-muted-foreground whitespace-pre-wrap"
                              data-testid="bot-internal-notes"
                              title="Nur intern sichtbar, der Kunde sieht das nicht"
                            >
                              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-sans font-semibold mb-1">
                                Team-Notiz · nur intern
                              </div>
                              {b.internalNotes}
                            </div>
                          )}
                        </div>
                        {!isLeft && (
                          <div
                            className={`w-7 h-7 rounded-full text-white flex items-center justify-center ${
                              b.kind === "bot" ? "bg-primary" : "bg-emerald-600"
                            }`}
                          >
                            {b.kind === "bot" ? (
                              <Robot size={14} weight="fill" />
                            ) : (
                              <UserIcon size={14} weight="fill" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={chatBottomRef} />
              </div>
            </div>

            {/* Reply-Input — immer da */}
            <div className="bg-white border-t border-border-soft p-3 sm:p-4 sticky bottom-0">
              <div className="max-w-2xl mx-auto">
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="owner-reply-textarea"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={4}
                    placeholder={`Antwort an ${detail.sender_name} schreiben — geht über ${
                      CHANNEL_LABEL[detail.channel] || "Simulator"
                    } raus`}
                    className="flex-1 border border-border-soft rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y min-h-[100px] transition-all"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        handleSendOwnerReply();
                      }
                    }}
                  />
                  <button
                    data-testid="send-owner-reply-btn"
                    onClick={handleSendOwnerReply}
                    disabled={!reply.trim() || sending}
                    className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center disabled:opacity-40 hover:bg-primary/90 shrink-0"
                    aria-label="Senden"
                  >
                    <PaperPlaneTilt size={20} weight="fill" />
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5 text-right">
                  Strg/Cmd + Enter
                </div>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
